/**
 * Dashboard Page - Reflection & Progress View
 * 
 * This is the "Step Back" view where users can see their progress over time,
 * reflect on what they've accomplished, and understand their patterns.
 * 
 * NOT the daily action page — that's Today's Focus (/today).
 */

import React, { useState, useEffect } from 'react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import {
  TrendingUp, RefreshCw, Calendar, CheckCircle2, Star,
  Target, ListTodo, BarChart3, Clock,
  ArrowRight, Sparkles, AlertTriangle
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { DailyActivityLog } from '../components/activity/DailyActivityLog'
import { UnifiedActivityFeed } from '../components/activity/UnifiedActivityFeed'
import { SkeletonList } from '../components/ui/Skeleton'

// ============ Types ============

interface HierarchyBreakdownItem {
  total: number
  completed: number
  this_period: number
}

interface AnalyticsData {
  success: boolean
  period: string
  start_date: string
  end_date: string
  hierarchy_overview: {
    total_items: number
    items_created_this_period: number
    breakdown: {
      dreams: HierarchyBreakdownItem
      goals: HierarchyBreakdownItem
      tasks: HierarchyBreakdownItem
      skills: HierarchyBreakdownItem
      habits: HierarchyBreakdownItem
      action_items: HierarchyBreakdownItem
      aspirations?: HierarchyBreakdownItem
    }
  }
  completion: {
    total_completed: number
    overall_rate: number
  }
  activity_trends: Array<{
    week: string
    created: number
    completed: number
  }>
}

type PeriodType = 'week' | 'month' | 'quarter'

// ============ Component ============

export const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [period, setPeriod] = useState<PeriodType>('month')
  const [error, setError] = useState<string | null>(null)

  useProvideFeedbackContext({ pageType: 'dashboard', extra: { period } })

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch analytics data
      const response = await fetch(`/api/user-analytics/overview?period=${period}`, {
        credentials: 'include'
      })
      
      // 2026-08-29 defect: a failed request left analyticsData AND error null,
      // so the page fell through to the "nothing here yet" empty state — which
      // claims we looked and found nothing. A failure must read as a failure.
      if (!response.ok) {
        setAnalyticsData(null)
        setError(`We couldn't load your progress (server said ${response.status}).`)
        return
      }

      const result = await response.json()
      if (!result.success) {
        setAnalyticsData(null)
        setError("We couldn't load your progress just now.")
        return
      }

      setAnalyticsData(result)
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setAnalyticsData(null)
      setError("We couldn't reach the server to load your progress.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [period])

  const getPeriodLabel = () => {
    switch (period) {
      case 'week': return 'This Week'
      case 'month': return 'This Month'
      case 'quarter': return 'This Quarter'
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 p-6" aria-busy="true" aria-label="Loading dashboard">
        <SkeletonList count={4} />
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5 p-4 md:p-5">
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-primary flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-brand" />
            Your Progress
          </h1>
          <p className="text-secondary mt-1">
            Step back and see how far you've come
            {/* Motivation left the sidebar in the Three Rooms shell — this
                is its home now until the two pages merge. */}
            {' · '}
            <Link to="/motivation" className="text-brand hover:underline">
              Motivation
            </Link>
          </p>
        </div>
        
        {/* Period Selector */}
        <div className="flex items-center gap-3">
          <div className="flex bg-surface-alt rounded-lg p-1">
            {(['week', 'month', 'quarter'] as PeriodType[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 min-h-touch text-sm font-medium rounded-md transition-all ${
                  period === p
                    ? 'bg-surface text-primary shadow-sm'
                    : 'text-secondary hover:text-primary'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick Link to Today */}
      <Card className="bg-brand-subtle border-brand-subtle">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-brand" />
              <span className="text-secondary">
                Ready to focus on today?
              </span>
            </div>
            <Link to="/today">
              <Button size="sm" className="bg-brand text-on-brand">
                Go to Today's Focus
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* An error is not an empty state: say we couldn't look, and offer the
          retry rather than implying there is nothing here (2026-08-29). */}
      {error && (
        <Card className="border-warn/30 bg-warn/10">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-warn">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <span>{error} Nothing has been lost — we just couldn't read it.</span>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm" className="min-h-touch">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {analyticsData && (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-ok/10 rounded-full">
                    <CheckCircle2 className="w-6 h-6 text-ok" />
                  </div>
                  <div>
                    <p className="text-sm text-secondary">Completed {getPeriodLabel()}</p>
                    <p className="text-2xl font-semibold text-primary">
                      {analyticsData.completion.total_completed}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-brand-subtle rounded-full">
                    <Sparkles className="w-6 h-6 text-brand" />
                  </div>
                  <div>
                    <p className="text-sm text-secondary">New Items Created</p>
                    <p className="text-2xl font-semibold text-primary">
                      {analyticsData.hierarchy_overview.items_created_this_period}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-brand-subtle rounded-full">
                    <TrendingUp className="w-6 h-6 text-brand" />
                  </div>
                  {/* 2026-08-29: was "Completion Rate {n}%" — a score on the
                      user's life, the same framing the letter-graded
                      Productivity Score was retired for. Same data, told as
                      what they are carrying rather than a fraction of a
                      target: how much they finished is the card above. */}
                  <div>
                    <p className="text-sm text-secondary">On your map</p>
                    <p className="text-2xl font-semibold text-primary">
                      {analyticsData.hierarchy_overview.total_items}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      dreams, goals, tasks and habits together
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown by Type - Task focused, clickable */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-brand" />
                Progress by Category
              </CardTitle>
              {/* 2026-08-29: said "double-click" for rows that are ordinary
                  single-click links (and the double-click handler forced a
                  full page reload). Instruction now matches the behaviour. */}
              <p className="text-sm text-secondary mt-1">
                Pick a row to see those items
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Order: tasks first (most actionable), then the containers. */}
                {(['tasks', 'goals', 'skills', 'habits', 'dreams'] as const).map((type) => {
                  const data = analyticsData.hierarchy_overview.breakdown[type]
                  if (!data) return null
                  
                  const icons: Record<string, React.ReactNode> = {
                    tasks: <ListTodo className="w-4 h-4" />,
                    goals: <Target className="w-4 h-4" />,
                    skills: <Sparkles className="w-4 h-4" />,
                    habits: <Sparkles className="w-4 h-4" />,
                    dreams: <Star className="w-4 h-4" />,
                  }
                  const colors: Record<string, string> = {
                    tasks: 'text-ok bg-ok/10',
                    goals: 'text-brand bg-brand-subtle',
                    skills: 'text-info bg-info/10',
                    habits: 'text-brand bg-brand-subtle',
                    dreams: 'text-brand bg-brand-subtle',
                  }
                  const links: Record<string, string> = {
                    tasks: '/tasks',
                    goals: '/goals',
                    skills: '/skills',
                    habits: '/habits',
                    dreams: '/dreams',
                  }
                  const progress = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0
                  
                  return (
                    <Link
                      key={type}
                      to={links[type]}
                      className="block"
                    >
                      <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-surface-alt transition-colors cursor-pointer">
                        <div className={`p-2 rounded-lg ${colors[type]}`}>
                          {icons[type]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-secondary capitalize">
                              {type.replace('_', ' ')}
                            </span>
                            <span className="text-lg font-bold text-primary">
                              {progress}%
                            </span>
                          </div>
                          <div className="w-full bg-soft rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                progress >= 70 ? 'bg-ok' :
                                progress >= 40 ? 'bg-brand' :
                                progress > 0 ? 'bg-warn' : 'bg-soft'
                              }`}
                              style={{ width: `${Math.max(progress, 2)}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-muted">
                              {data.completed} of {data.total} complete
                            </span>
                            {data.this_period > 0 && (
                              <span className="text-xs text-ok">
                                +{data.this_period} new
                              </span>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted" />
                      </div>
                    </Link>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Activity Trend */}
          {analyticsData.activity_trends && analyticsData.activity_trends.length > 0 && (
            <Card>
              <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand" />
                Recent Activity
              </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analyticsData.activity_trends.slice(0, 4).map((trend, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-surface-alt rounded-lg">
                      <span className="text-sm text-secondary">
                        Week of {trend.week}
                      </span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-brand">
                          +{trend.created} created
                        </span>
                        <span className="text-ok">
                          ✓ {trend.completed} completed
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Empty State */}
      {!analyticsData && !error && (
        <Card className="p-12 text-center">
          <BarChart3 className="w-12 h-12 text-muted mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-primary mb-2">
            Your progress will show here
          </h3>
          <p className="text-secondary mb-6">
            As you work on your goals and tasks, you'll see your progress over time.
          </p>
          <Link to="/today">
            <Button className="bg-brand text-on-brand">
              Start with Today's Focus
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </Card>
      )}

      {/* The rolling log: you AND the assistant, one stream (2026-08-25). */}
      <Card>
        <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand" />
                The running log
              </CardTitle>
        </CardHeader>
        <CardContent>
          <UnifiedActivityFeed />
        </CardContent>
      </Card>

      {/* Daily Activity Log (user-only detail view) */}
      <Card>
        <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand" />
                Daily Activity
              </CardTitle>
        </CardHeader>
        <CardContent>
          <DailyActivityLog compact={false} showSummary={true} />
        </CardContent>
      </Card>

      {/* Encouragement Footer */}
      <div className="text-center py-6 text-secondary">
        <p className="italic">Progress isn't always visible. Trust the process.</p>
      </div>

    </div>
  )
}
